import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import type { InboxConversationSummary } from "@/hooks/use-inbox-conversations";
import { Button } from "@/components/ui/button";
import { Archive, Clock, MailOpen } from "lucide-react";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";

function relativeTime(
  iso: string,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("relativeTime.justNow");
  if (min < 60) return t("relativeTime.minutes", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("relativeTime.hours", { count: hr });
  const day = Math.floor(hr / 24);
  if (day < 7) return t("relativeTime.days", { count: day });
  return formatDate(d, locale, { month: "short", day: "numeric" });
}

interface Props {
  conversations: InboxConversationSummary[];
  selectedId?: string | null;
  onSelect: (convId: string) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  /** Extra content under the "No conversations yet." empty state (e.g. a connect-account pointer). */
  emptyExtra?: React.ReactNode;
  /** When provided, a hover "Hide" (archive) button is shown on each row. */
  onArchive?: (convId: string) => void;
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isLoading,
  emptyExtra,
  onArchive,
}: Props) {
  const { t } = useTranslation("inbox");
  const locale = useLocale();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
        <MailOpen size={32} className="mb-3 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">{t("conversationList.empty")}</p>
        {emptyExtra}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {conversations.map((c) => {
        const isSelected = c.id === selectedId;
        const isUnread = c.unreadCount > 0;
        const initial = (c.participantName ?? c.participantId).charAt(0).toUpperCase();
        return (
          <div
            key={c.id}
            className={cn(
              "group relative border-b border-border transition-colors",
              isSelected ? "bg-accent" : "hover:bg-surface-2",
            )}
          >
          <button
            type="button"
            onClick={() => onSelect(c.id)}
            className="flex w-full items-start gap-3 px-3 py-3 text-left"
          >
            <span className="w-9 h-9 rounded-full bg-brand-subtle text-brand-subtle-text flex items-center justify-center text-xs font-semibold flex-shrink-0">
              {initial}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn(
                    "truncate text-[13px]",
                    isUnread ? "font-semibold text-foreground" : "text-foreground",
                  )}
                >
                  {c.participantName ?? c.participantId}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground flex-shrink-0">
                  <PlatformIcon platform={c.platform} size={12} />
                  {relativeTime(c.lastMessageAt, t, locale)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <p
                  className={cn(
                    "truncate text-[12px]",
                    isUnread ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {c.lastMessageText || <span className="italic">{t("conversationList.noPreview")}</span>}
                </p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {c.snoozedUntil && <Clock size={12} className="text-muted-foreground" />}
                  {isUnread && (
                    <span className="inline-flex items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground min-w-[16px] h-[16px] leading-none">
                      {c.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>
          {onArchive && (
            <button
              type="button"
              aria-label={t("archive.hide")}
              title={t("archive.hide")}
              onClick={(e) => {
                e.stopPropagation();
                onArchive(c.id);
              }}
              className="absolute right-2 top-2 hidden rounded-md bg-surface-1/90 p-1 text-muted-foreground shadow-sm hover:bg-surface-3 hover:text-foreground group-hover:block"
            >
              <Archive size={14} />
            </button>
          )}
          </div>
        );
      })}
      {hasNextPage && (
        <div className="p-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            {isFetchingNextPage ? t("conversationList.loading") : t("conversationList.loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
}
